import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useMe, type PlaybackPresentation } from "../api/queries";
import { request } from "../api/client";
import type { TimelineGap } from "../api/types";
import {
  currentPlaybackObservation,
  playbackMediaSpans,
  playbackMediaUs,
  sourceMsAt,
  subscribePlaybackObservation,
  type PlaybackObservation,
} from "../player/useAudioSession";
import { nativeRuntime } from "../platform/native";

export type Span = [startMs: number, endMs: number];

const FLUSH_MS = 15_000;
const MAX_BATCH = 200;
const MIN_SPAN_MS = 10_000;
const HEARD_KEY = "jtarchive.heard";

let sessionSpans: Span[] = [];
let sessionGaps: TimelineGap[] = [];
let listeningFrom: number | null = null;

export const heardThisSession = (): readonly TimelineGap[] => sessionGaps;
export const listeningSinceMs = (): number | null => listeningFrom;

const counts = ([start, end]: Span): boolean => end - start >= MIN_SPAN_MS;

export function sendable(outbox: readonly Span[], session: readonly Span[]): Span[] {
  const heard = session.filter(counts);
  return outbox.filter((span) => heard.some(([start, end]) => span[0] >= start && span[1] <= end));
}

export function mergeSpans(spans: readonly Span[]): Span[] {
  const merged: Span[] = [];
  for (const [start, end] of [...spans].sort((a, b) => a[0] - b[0] || a[1] - b[1])) {
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const previous = merged.at(-1);
    if (previous && start <= previous[1]) previous[1] = Math.max(previous[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

export function unseenSpans(current: readonly Span[], seen: readonly Span[]): Span[] {
  const known = mergeSpans(seen);
  const unseen: Span[] = [];
  for (const [start, end] of mergeSpans(current)) {
    let cursor = start;
    for (const [knownStart, knownEnd] of known) {
      if (knownEnd <= cursor) continue;
      if (knownStart >= end) break;
      if (knownStart > cursor) unseen.push([cursor, Math.min(knownStart, end)]);
      cursor = Math.max(cursor, knownEnd);
      if (cursor >= end) break;
    }
    if (cursor < end) unseen.push([cursor, end]);
  }
  return unseen;
}

export function sourceSpansForMedia(
  presentation: PlaybackPresentation,
  mediaSpans: readonly Span[],
): Span[] {
  const sourceSpans: Span[] = [];
  for (const piece of presentation.map.pieces) {
    if (!piece.source_start || !piece.source_end) continue;
    const sourceStart = Date.parse(piece.source_start);
    const sourceEnd = Date.parse(piece.source_end);
    const mediaDuration = piece.media_end_us - piece.media_start_us;
    if (
      !Number.isFinite(sourceStart) ||
      !Number.isFinite(sourceEnd) ||
      sourceEnd <= sourceStart ||
      mediaDuration <= 0
    ) {
      continue;
    }
    for (const [mediaStart, mediaEnd] of mediaSpans) {
      const start = Math.max(mediaStart, piece.media_start_us);
      const end = Math.min(mediaEnd, piece.media_end_us);
      if (end <= start) continue;
      const scale = (sourceEnd - sourceStart) / mediaDuration;
      sourceSpans.push([
        Math.round(sourceStart + (start - piece.media_start_us) * scale),
        Math.round(sourceStart + (end - piece.media_start_us) * scale),
      ]);
    }
  }
  return mergeSpans(sourceSpans);
}

function playedRanges(audio: HTMLMediaElement): Span[] {
  const ranges: Span[] = [];
  for (let index = 0; index < audio.played.length; index += 1) {
    ranges.push([
      Math.round(audio.played.start(index) * 1_000_000),
      Math.round(audio.played.end(index) * 1_000_000),
    ]);
  }
  return mergeSpans(ranges);
}

const isSpan = (raw: unknown): raw is Span =>
  Array.isArray(raw) &&
  raw.length === 2 &&
  raw.every((value) => typeof value === "number" && Number.isFinite(value));

function loadHeard(userId: number): Span[] {
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(`${HEARD_KEY}.${userId}`) ?? "[]");
    return Array.isArray(raw) ? mergeSpans(raw.filter(isSpan)) : [];
  } catch {
    return [];
  }
}

function saveHeard(userId: number): void {
  try {
    sessionStorage.setItem(`${HEARD_KEY}.${userId}`, JSON.stringify(sessionSpans));
  } catch {
    // The server remains the durable copy when tab storage is unavailable.
  }
}

interface Outbox {
  spans: Span[];
  sending: boolean;
}

interface Tracker {
  observation: PlaybackObservation;
  seen: Span[];
  played: Span[];
  pending: Span[];
  cleanup: () => void;
}

function refreshDerived(observation: PlaybackObservation | null): void {
  sessionGaps = sessionSpans.filter(counts).map(([start, end]) => ({
    start: new Date(start).toISOString(),
    end: new Date(end).toISOString(),
    seconds: (end - start) / 1000,
  }));
  if (!observation) {
    listeningFrom = null;
    return;
  }
  const mediaUs = playbackMediaUs(observation);
  const rawSourceMs = mediaUs === null ? null : sourceMsAt(observation.presentation, mediaUs);
  const sourceMs = rawSourceMs === null ? null : Math.round(rawSourceMs);
  listeningFrom =
    sourceMs === null
      ? null
      : (sessionSpans.find(([start, end]) => sourceMs >= start && sourceMs <= end)?.[0] ?? null);
}

function knownAudioSpans(observation: PlaybackObservation, spans: readonly Span[]): Span[] {
  const known: Span[] = [];
  for (const [spanStart, spanEnd] of spans) {
    const startS = spanStart / 1_000_000;
    let low = 0;
    let high = observation.clock.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (observation.clock[middle]!.audioStartS < startS) low = middle + 1;
      else high = middle;
    }
    let index = Math.max(0, low - 1);
    while (index > 0 && observation.clock[index - 1]!.audioEndS * 1_000_000 > spanStart) {
      index -= 1;
    }
    for (; index < observation.clock.length; index += 1) {
      const interval = observation.clock[index]!;
      const start = interval.audioStartS * 1_000_000;
      if (start >= spanEnd) break;
      const end = interval.audioEndS * 1_000_000;
      if (end > spanStart) known.push([Math.max(spanStart, start), Math.min(spanEnd, end)]);
    }
  }
  return mergeSpans(known);
}

function recordPlayed(tracker: Tracker, outbox: Outbox): void {
  const played = playedRanges(tracker.observation.audio);
  const freshAudio = unseenSpans(played, tracker.played);
  tracker.played = mergeSpans([...tracker.played, ...played]);
  const candidates = mergeSpans([...tracker.pending, ...freshAudio]);
  const known = knownAudioSpans(tracker.observation, candidates);
  tracker.pending = unseenSpans(candidates, known);
  const current = playbackMediaSpans(tracker.observation, candidates);
  const fresh = unseenSpans(current, tracker.seen);
  tracker.seen = mergeSpans([...tracker.seen, ...current]);
  if (fresh.length > 0) {
    const source = sourceSpansForMedia(tracker.observation.presentation, fresh);
    outbox.spans = mergeSpans([...outbox.spans, ...source]);
    sessionSpans = mergeSpans([...sessionSpans, ...source]);
  }
  refreshDerived(tracker.observation);
}

function reportListened(outbox: Outbox, client: QueryClient): void {
  if (outbox.sending) return;
  const batch = sendable(outbox.spans, sessionSpans).slice(0, MAX_BATCH);
  if (batch.length === 0) return;
  const sent = new Set(batch.map(([start, end]) => `${start}:${end}`));
  outbox.spans = outbox.spans.filter(([start, end]) => !sent.has(`${start}:${end}`));
  outbox.sending = true;
  void request("/api/listens", {
    method: "POST",
    body: JSON.stringify({
      spans: batch.map(([start_ms, end_ms]) => ({ start_ms, end_ms })),
    }),
    keepalive: true,
  })
    .then(() => client.invalidateQueries({ queryKey: ["timeline"], refetchType: "none" }))
    .catch(() => {
      outbox.spans = mergeSpans([...batch, ...outbox.spans]);
    })
    .finally(() => {
      outbox.sending = false;
    });
}

export function useListenedSync(): void {
  const { data: me } = useMe();
  const userId = me?.id ?? null;
  const client = useQueryClient();
  const browser = nativeRuntime() === null;

  useEffect(() => {
    if (!browser) return;
    if (userId === null) {
      sessionSpans = [];
      sessionGaps = [];
      listeningFrom = null;
      return;
    }

    sessionSpans = loadHeard(userId);
    refreshDerived(null);
    const outbox: Outbox = { spans: [], sending: false };
    let tracker: Tracker | null = null;

    const sample = () => {
      if (tracker) recordPlayed(tracker, outbox);
    };

    const attach = (observation: PlaybackObservation | null) => {
      const previous = tracker;
      if (previous) {
        recordPlayed(previous, outbox);
        previous.cleanup();
        tracker = null;
      }
      if (!observation) {
        refreshDerived(null);
        return;
      }
      const events = ["timeupdate", "pause", "seeked", "ended"] as const;
      for (const event of events) observation.audio.addEventListener(event, sample);
      const sameAttachment =
        previous !== null &&
        previous.observation.attachment === observation.attachment &&
        previous.observation.audio === observation.audio;
      const played = playedRanges(observation.audio);
      tracker = {
        observation,
        seen: sameAttachment ? previous.seen : playbackMediaSpans(observation, played),
        played: sameAttachment ? previous.played : played,
        pending: sameAttachment ? previous.pending : [],
        cleanup: () => {
          for (const event of events) observation.audio.removeEventListener(event, sample);
        },
      };
      if (sameAttachment) recordPlayed(tracker, outbox);
      else refreshDerived(observation);
    };

    const flush = () => {
      sample();
      reportListened(outbox, client);
    };
    const unsubscribe = subscribePlaybackObservation((next) => attach(next));
    attach(currentPlaybackObservation());
    const flusher = window.setInterval(flush, FLUSH_MS);
    const leaving = () => {
      flush();
      saveHeard(userId);
    };
    const visible = () => {
      if (document.visibilityState === "visible") flush();
    };
    window.addEventListener("pagehide", leaving);
    document.addEventListener("visibilitychange", visible);

    return () => {
      window.clearInterval(flusher);
      window.removeEventListener("pagehide", leaving);
      document.removeEventListener("visibilitychange", visible);
      flush();
      unsubscribe();
      tracker?.cleanup();
      saveHeard(userId);
    };
  }, [browser, client, userId]);
}
